// Push в приложении (Capacitor): регистрация FCM-токена на сервере и отзыв при выходе.
//
// Нативная часть живёт в `@studyhub/mobile` (init-push.ts) и доступна только в
// мобильных сборках: в web-бандле `__IS_CAPACITOR__ === false`, и ветки с
// импортом снимает DCE. Здесь — тонкий мост к ней, чтобы компонент и выход из
// аккаунта не знали про Capacitor. Браузерный Web Push — отдельно
// (`hooks/use-web-push-channel.ts`).
//
// ДО OXEM-549 нативный `initPushNotifications` экспортировался, но не вызывался
// ниоткуда (аудит 2026-06-01): токен приложения на сервер не попадал вовсе.

import { getDeviceId } from "@/lib/device-id";
import { createTrpcClient } from "@/lib/trpc-client";

type PushPermissionState = "granted" | "denied" | "prompt";

import { remindPushAppUpdate, showPushAppUpdateToast } from "@/lib/push-app-update";

interface RegisterTokenInput {
	platform: "ios" | "android";
	token: string;
	deviceId: string;
	firebaseProject?: "international-exchange-center";
}

interface MobilePush {
	getPushPermissionState(): Promise<PushPermissionState | null>;
	initPushNotifications(opts: {
		registerToken: (input: RegisterTokenInput) => Promise<unknown>;
		deviceId: string;
		force?: boolean;
		requestPermission?: boolean;
		onAppUpdateRequired?: () => void;
	}): Promise<"requested" | "app_update_required" | "denied" | "unavailable">;
	isPushAppUpdateRequired(): Promise<boolean>;
	resetPushRegistration(): void;
}

// ЛИТЕРАЛЬНЫЙ specifier и @ts-expect-error — по тем же причинам, что в main.tsx.
function loadMobilePush(): Promise<MobilePush | null> {
	if (!__IS_CAPACITOR__) return Promise.resolve(null);
	// @ts-expect-error — @studyhub/mobile резолвится только Vite-алиасом мобильных режимов.
	return (import("@studyhub/mobile") as unknown as Promise<MobilePush>).catch((err) => {
		console.error("[native-push] @studyhub/mobile недоступен:", err);
		return null;
	});
}

/** Разрешение на push в приложении; `null` — не приложение. */
export async function getNativePushPermission(): Promise<PushPermissionState | null> {
	const mobile = await loadMobilePush();
	return mobile ? mobile.getPushPermissionState() : null;
}

/** Нативная сборка на прежнем Firebase-проекте: push ей доставить нечем. */
export async function isNativePushUpdateRequired(): Promise<boolean> {
	const mobile = await loadMobilePush();
	return mobile ? mobile.isPushAppUpdateRequired() : false;
}

/**
 * Зарегистрировать устройство на push. `requestPermission: false` — только если
 * разрешение уже выдано (тихое восстановление при запуске). Старая сборка
 * (`app_update_required`, от нативного слоя или от сервера) разрешения не просит —
 * человеку показывается «обновите приложение».
 */
export async function enableNativePush(
	registerToken: (input: RegisterTokenInput) => Promise<unknown>,
	opts: { requestPermission: boolean },
): Promise<void> {
	const mobile = await loadMobilePush();
	if (!mobile) return;
	const result = await mobile.initPushNotifications({
		registerToken,
		deviceId: getDeviceId(),
		requestPermission: opts.requestPermission,
		onAppUpdateRequired: showPushAppUpdateToast,
	});
	if (result === "app_update_required") showPushAppUpdateToast();
}

/**
 * Стартовая проверка: сборка старая → тем, у кого push уже разрешён, раз в сутки
 * напоминаем обновить приложение. Возвращает `true` для старой сборки — вызывающий
 * дальше не регистрирует и подводку не показывает.
 */
export async function handleOutdatedNativePush(
	permission: "granted" | "denied" | "prompt" | null,
): Promise<boolean> {
	if (!(await isNativePushUpdateRequired())) return false;
	if (permission === "granted") remindPushAppUpdate();
	return true;
}

/**
 * Отозвать токен устройства у уходящего аккаунта — ДО revoke сессии (нужен
 * bearer). Иначе push прежнего пользователя продолжал бы приходить на устройство
 * следующего. Best-effort: сбой не мешает выходу.
 */
export async function disableNativePush(): Promise<void> {
	const mobile = await loadMobilePush();
	if (!mobile) return;
	try {
		await createTrpcClient().pushTokens.unregisterDevice.mutate({ deviceId: getDeviceId() });
	} catch (err) {
		console.warn("[native-push] unregisterDevice failed:", err);
	} finally {
		mobile.resetPushRegistration();
	}
}
